import React from 'react';
import { isBlank } from 'utils/helperMethods';
import { any } from 'ramda';

import { Route, Switch, Redirect, withRouter } from 'react-router';
import { appRoutes } from 'routes';
import UserPresenter from 'presenters/UserPresenter';
import useApp from 'reducers/hooks/useApp';

import { extractPopupPathFromUrlToSearchParam, isMatchedPath } from 'utils/location';
import getHomePageLocals from 'components/NonAuthHomePage/data';

import BuyerMessages from 'components/BuyerMessages';
import LoanOfficerMessages from 'components/LoanOfficerMessages';
import Calculators from 'components/Calculators';
import TermsPage from 'components/Terms';
import PrivacyPage from 'components/Privacy';
import Faqs from 'components/Faqs';
import LoanOfficerDashboard from 'components/LoanOfficerDashboard';
import LoanOfficerRealEstateAgents from 'components/LoanOfficerRealEstateAgents';
import SurveyPage from 'components/SurveyPage';
import BuyerLoanOfficerSearchSurvey from 'components/BuyerLoanOfficerSearchSurvey';
import NonAuthSurveyPage from 'components/NonAuthSurveyPage';
import EmbedSurveyPage from 'components/EmbedSurveyPage';
import EmbedCalculator from 'components/EmbedCalculator';
import TodaysRatesPage from 'components/TodaysRates';
import BuyerSettings from 'components/BuyerSettings';
import AboutUs from 'components/AboutUs';
import LoanOfficerSettings from 'components/LoanOfficerSettings';
import QuizPage from 'components/QuizPage';
import NonAuthHomePage from 'components/NonAuthHomePage';
import AdminDashboard from 'components/AdminDashboard';
import AdminLoanOfficer from 'components/AdminLoanOfficer';
import RealEstateAgentSettingsPage from 'components/RealEstateAgentSettingsPage';
import RealEstateAgentDashboard from 'components/RealEstateAgentDashboard';
import RealEstateAgentLoanOfficers from 'components/RealEstateAgentLoanOfficers';
import ReferralRealEstateAgent from 'components/ReferralRealEstateAgent';

import { ROOT_PATHS, POPUP_ROUTES } from './const/router';
import AppLayout from './layouts/AppLayout';
import NonAuthLayout from './layouts/NonAuthLayout';

const Routes = withRouter(props => {
  const { currentUser } = useApp();

  const isBuyer = UserPresenter.isBuyer(currentUser);

  const isLoanOfficer = UserPresenter.isLoanOfficer(currentUser);
  const isRealEstateAgent = UserPresenter.isRealEstateAgent(currentUser);
  const isNotAuthenticated = isBlank(currentUser);
  const isAdmin = UserPresenter.isAdmin(currentUser);

  const { pathname, search } = props.location;
  const isPopupRoutes = pathname.split('/').some(e => POPUP_ROUTES.includes(e));

  if (isPopupRoutes) {
    const searchParams = extractPopupPathFromUrlToSearchParam(pathname, search);
    return (
      <Redirect
        to={{
          pathname: appRoutes.rootPath(),
          search: searchParams,
        }}
      />
    );
  }

  const commonRoutes = [
    <Route key={appRoutes.termsPath()} path={appRoutes.termsPath()} component={TermsPage} />,
    <Route key={appRoutes.privacyPath()} path={appRoutes.privacyPath()} component={PrivacyPage} />,
    <Route key={appRoutes.faqsPath()} path={appRoutes.faqsPath()} component={Faqs} />,
    <Route key={appRoutes.quizPath()} path={appRoutes.quizPath()} component={QuizPage} />,
    <Route key={appRoutes.aboutUsPath()} path={appRoutes.aboutUsPath()} component={AboutUs} />,
    <Route key={appRoutes.todaysRatesPath()} path={appRoutes.todaysRatesPath()} component={TodaysRatesPage} />,
  ];

  const nonLayoutPath = [
    appRoutes.referralRealEstateAgentPath(':slug'),
    appRoutes.surveyPathEmbed(),
    appRoutes.calculatorPathEmbed(),
  ];
  const isNonLayoutPagePath = any(isMatchedPath(pathname), nonLayoutPath);

  if (isNotAuthenticated) {
    const isRoot = ROOT_PATHS.some(r => props.location.pathname === r);
    const { rootLocals, loanOfficerLocals, realEstateAgentLocals } = getHomePageLocals();

    if (isRoot) {
      return (
        <Switch>
          <Route
            exact
            path={appRoutes.loanOfficerPath()}
            render={() => <NonAuthHomePage locals={loanOfficerLocals} />}
          />
          <Route
            exact
            path={appRoutes.realEstateAgentPath()}
            render={() => <NonAuthHomePage locals={realEstateAgentLocals} />}
          />
          <Route path={appRoutes.rootPath()} render={() => <NonAuthHomePage locals={rootLocals} />} />
        </Switch>
      );
    }

    if (isNonLayoutPagePath) {
      return (
        <Switch>
          <Route path={appRoutes.referralRealEstateAgentPath(':slug')} component={ReferralRealEstateAgent} />
          <Route path={appRoutes.surveyPathEmbed()} component={EmbedSurveyPage} />
          <Route path={appRoutes.calculatorPathEmbed()} component={EmbedCalculator} />
          <Redirect to={appRoutes.rootPath()} />
        </Switch>
      );
    }

    return (
      <NonAuthLayout>
        <Switch>
          <Route exact path={appRoutes.calculatorsPath()} component={Calculators} />
          <Route exact path={appRoutes.surveyPath()} component={NonAuthSurveyPage} />
          <Route path={appRoutes.loanOfficerSearchSurveyRootPath()} component={BuyerLoanOfficerSearchSurvey} />
          {commonRoutes}
          <Redirect to={appRoutes.rootPath()} />
        </Switch>
      </NonAuthLayout>
    );
  }

  if (isBuyer) {
    return (
      <AppLayout>
        <Switch>
          <Route path={appRoutes.surveyPath()} component={SurveyPage} />
          <Route path={appRoutes.calculatorsPath()} component={Calculators} />
          <Route path={appRoutes.messagesPath()} component={BuyerMessages} />
          <Route path={appRoutes.settingsPath()} component={BuyerSettings} />
          {commonRoutes}
          <Redirect to={appRoutes.messagesPath()} />
        </Switch>
      </AppLayout>
    );
  }

  if (isLoanOfficer) {
    return (
      <AppLayout>
        <Switch>
          <Route path={appRoutes.messagesPath()} component={LoanOfficerMessages} />
          <Route path={appRoutes.loanOfficerFindRealEstateAgents()} component={LoanOfficerRealEstateAgents} />
          <Route path={appRoutes.dashboardPath()} component={LoanOfficerDashboard} />
          <Route path={appRoutes.settingsPath()} component={LoanOfficerSettings} />
          {commonRoutes}
          <Redirect to={appRoutes.messagesPath()} />
        </Switch>
      </AppLayout>
    );
  }

  if (isAdmin) {
    return (
      <AppLayout>
        <Switch>
          <Route exact path={appRoutes.adminRootPath()} component={AdminDashboard} />
          <Route exact path={appRoutes.adminLoanOfficerPath(':id')} component={AdminLoanOfficer} />
          {commonRoutes}
          <Redirect to={appRoutes.adminRootPath()} />
        </Switch>
      </AppLayout>
    );
  }

  if (isRealEstateAgent) {
    return (
      <AppLayout>
        <Switch>
          <Route exact path={appRoutes.rootPath()} component={RealEstateAgentDashboard} />
          <Route exact path={appRoutes.loanOfficersPath()} component={RealEstateAgentLoanOfficers} />
          {commonRoutes}
          <Route path={appRoutes.settingsPath()} component={RealEstateAgentSettingsPage} />
          <Redirect to={appRoutes.rootPath()} />
        </Switch>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Switch>
        {commonRoutes}
        <Redirect to={appRoutes.rootPath()} />
      </Switch>
    </AppLayout>
  );
});

export default Routes;



// WEBPACK FOOTER //
// ./src/AppRoutes.js